import { Plugin } from '../../core/plugin-interface.js';
import { OptimizedMutationObserver } from '../../utils/observer-utils.js';
import { getAllImages } from '../../utils/dom-utils.js';
import { isSafari } from '../../utils/browser-utils.js';
/**
 * ImageProxyPlugin - Optimizes images through a proxy service
 * Implements Patent 1: Adaptive Image Delivery System
 */
export class ImageProxyPlugin extends Plugin {
    constructor(config) {
        super({
            ...config
        });
        this.observer = null;
        this.name = 'ImageProxyPlugin';
        this.config = {
            ...config,
            proxyEndpoint: config.proxyEndpoint ?? 'https://production-image-proxy.reproio.com',
            proxyToken: config.proxyToken ?? 'e59f89e2-8cb4-4335-aee8-6ffbf2db8685',
            // Whitelist: only proxy these image formats
            supportedExtensions: config.supportedExtensions ?? ['.png', '.jpg', '.jpeg'],
            // Exclude patterns for edge cases (already proxied, data URLs, etc.)
            excludePatterns: config.excludePatterns ?? [
                /^data:/i,
                /^blob:/i,
                /production-image-proxy\.reproio\.com/i,
            ],
        };
        this.formatSupport = { webp: false, avif: false };
        this.interceptedImages = new WeakSet();
    }
    /**
     * Initialize the plugin
     */
    async init() {
        const startTime = performance.now();
        try {
            // Check if Safari - skip optimization
            if (isSafari()) {
                this.log('Safari detected - image optimization disabled', 'debug');
                return;
            }
            // Detect format support
            this.formatSupport = await this.detectFormatSupport();
            this.log(`Format support - WebP: ${this.formatSupport.webp}, AVIF: ${this.formatSupport.avif}`, 'debug');
            // Set up image interception
            this.setupImageInterception();
            // Set up mutation observer for dynamic images
            this.setupMutationObserver();
        }
        finally {
            this.metrics.initTime = performance.now() - startTime;
        }
    }
    /**
     * Execute plugin optimization
     */
    async execute() {
        const startTime = performance.now();
        try {
            // Check if Safari - skip processing
            if (isSafari()) {
                this.log('Safari detected - skipping image processing', 'debug');
                return;
            }
            // Get all images and apply loading attributes to ALL of them
            const allImages = getAllImages();
            // Apply loading attributes to all images in batches to avoid blocking main thread
            await this.batchProcess(allImages, 10, (batch) => {
                batch.forEach(img => this.applyLoadingAttributes(img));
            });
            // Filter images for proxying
            const imagesToProxy = allImages.filter(img => !this.isProcessed(img) &&
                this.shouldProxy(img.src));
            // Find images with data-src attributes (lazy loading candidates)
            const lazyImages = Array.from(document.querySelectorAll('img[data-src], img[data-lazy-src], img[data-original]'))
                .filter((img) => img instanceof HTMLImageElement)
                .filter(img => !this.isProcessed(img));
            this.log(`Applied loading attributes to ${allImages.length} images. Found ${imagesToProxy.length} to proxy and ${lazyImages.length} lazy images`, 'debug');
            // Process regular images for proxying in batches to avoid blocking main thread
            if (imagesToProxy.length > 0) {
                await this.batchProcess(imagesToProxy, 5, (batch) => {
                    batch.forEach(img => this.optimizeImage(img));
                });
            }
            // Take over lazy loading images
            if (lazyImages.length > 0) {
                await this.batchProcess(lazyImages, 5, (batch) => {
                    batch.forEach(img => this.takeoverLazyImage(img));
                });
            }
        }
        finally {
            this.metrics.executeTime = performance.now() - startTime;
        }
    }
    /**
     * Set up image interception to catch dynamically created images
     */
    setupImageInterception() {
        // Intercept Image constructor
        const OriginalImage = window.Image;
        const plugin = this;
        window.Image = function (...args) {
            const img = new OriginalImage(...args);
            // Override src setter
            const originalSrcDescriptor = Object.getOwnPropertyDescriptor(img.constructor.prototype, 'src');
            if (originalSrcDescriptor) {
                Object.defineProperty(img, 'src', {
                    get: originalSrcDescriptor.get,
                    set: function (value) {
                        if (value && !plugin.interceptedImages.has(this) && plugin.shouldProxy(value)) {
                            plugin.interceptedImages.add(this);
                            value = plugin.generateOptimizedUrl(value);
                            // Set up error handling
                            plugin.setupErrorHandling(this, value);
                        }
                        originalSrcDescriptor.set.call(this, value);
                    }
                });
            }
            return img;
        };
        // Maintain prototype chain
        window.Image.prototype = OriginalImage.prototype;
    }
    /**
     * Set up mutation observer for DOM changes
     */
    setupMutationObserver() {
        this.observer = new OptimizedMutationObserver((mutations) => {
            const images = [];
            mutations.forEach(mutation => {
                mutation.addedNodes.forEach(node => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        if (node.tagName === 'IMG') {
                            images.push(node);
                        }
                        else if ('querySelectorAll' in node) {
                            const imgs = node.querySelectorAll('img');
                            images.push(...Array.from(imgs));
                        }
                    }
                });
            });
            // Process found images - apply attributes and proxy if applicable
            images.forEach(img => {
                // Apply loading attributes to ALL dynamically added images
                this.applyLoadingAttributes(img);
                if (!this.isProcessed(img)) {
                    // Check if this is a lazy loading image with data-src
                    const dataSrc = this.getLazySrc(img);
                    if (dataSrc) {
                        // This is a lazy loading image - take it over
                        this.takeoverLazyImage(img);
                    }
                    else if (img.src && this.shouldProxy(img.src)) {
                        // This is a regular image - proxy it
                        this.optimizeImage(img);
                    }
                }
            });
        }, {
            debounceMs: 10,
            batchSize: 20
        });
        this.observer.observe(document.documentElement, {
            childList: true,
            subtree: true
        });
    }
    /**
     * Get the lazy source URL from an image's data attributes
     */
    getLazySrc(img) {
        return img.dataset.src ||
            img.dataset.lazySrc ||
            img.dataset.original ||
            img.getAttribute('data-src') ||
            img.getAttribute('data-lazy-src') ||
            img.getAttribute('data-original');
    }
    /**
     * Check if an element is above the fold (visible in the initial viewport)
     */
    isAboveTheFold(img) {
        const rect = img.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        // Image is above the fold if its top edge is within the viewport
        return rect.top < viewportHeight;
    }
    /**
     * Check if image is in a non-first swiper slide (should be lazy loaded)
     */
    isInNonFirstSwiperSlide(img) {
        const swiperSlide = img.closest('.swiper-slide');
        if (!swiperSlide) {
            return false;
        }
        // Check if this is the first slide
        const parent = swiperSlide.parentElement;
        if (!parent) {
            return false;
        }
        const slides = parent.querySelectorAll('.swiper-slide');
        const slideIndex = Array.from(slides).indexOf(swiperSlide);
        // Return true if this is NOT the first slide (index > 0)
        return slideIndex > 0;
    }
    /**
     * Apply loading, decoding, and fetchpriority attributes to an image for better performance
     */
    applyLoadingAttributes(img) {
        // Always apply async decoding to avoid blocking the main thread
        img.decoding = 'async';
        // Check if image is in a non-first swiper slide - these should always be lazy
        const isNonFirstSlide = this.isInNonFirstSwiperSlide(img);
        if (isNonFirstSlide) {
            // Non-first swiper slides: force lazy loading and low priority
            img.loading = 'lazy';
            img.fetchPriority = 'low';
        }
        else {
            // Normal images: respect existing attributes
            if (!img.hasAttribute('loading') && !this.isAboveTheFold(img)) {
                img.loading = 'lazy';
            }
            if (!img.hasAttribute('fetchpriority')) {
                if (this.isAboveTheFold(img)) {
                    img.fetchPriority = 'high';
                }
                else {
                    img.fetchPriority = 'low';
                }
            }
        }
    }
    /**
     * Take over lazy loading for images with data-src attributes
     */
    takeoverLazyImage(img) {
        const dataSrc = this.getLazySrc(img);
        if (!dataSrc || !this.shouldProxy(dataSrc)) {
            return;
        }
        // Skip if already processing
        if (img.dataset.optimizing === 'true') {
            return;
        }
        this.log(`Taking over lazy image: ${dataSrc}`, 'debug');
        // Remove data-src to prevent other lazy loading scripts from interfering
        if (img.dataset.src)
            delete img.dataset.src;
        if (img.dataset.lazySrc)
            delete img.dataset.lazySrc;
        if (img.dataset.original)
            delete img.dataset.original;
        img.removeAttribute('data-src');
        img.removeAttribute('data-lazy-src');
        img.removeAttribute('data-original');
        // Store original for fallback
        img.dataset.originalSrc = dataSrc;
        img.dataset.optimizing = 'true';
        // Wait for format support detection
        if (!this.formatSupport.webp && !this.formatSupport.avif) {
            setTimeout(() => this.takeoverLazyImage(img), 100);
            return;
        }
        // Generate optimized URL
        const optimizedUrl = this.generateOptimizedUrl(dataSrc);
        // Only proceed if URL generation was successful
        if (optimizedUrl === dataSrc) {
            // Fallback to original data-src
            img.src = dataSrc;
            delete img.dataset.optimizing;
            this.markProcessed(img);
            return;
        }
        // Set up error handling before changing src
        this.setupErrorHandling(img, dataSrc);
        // Load the optimized image
        img.src = optimizedUrl;
        // Mark as processed
        this.markProcessed(img);
        delete img.dataset.optimizing;
        this.log(`Lazy image optimized: ${dataSrc} -> ${optimizedUrl}`, 'debug');
    }
    /**
     * Optimize a single image by proxying it
     */
    optimizeImage(img) {
        if (!img.src || this.isProxiedUrl(img.src)) {
            return;
        }
        // Skip if already processing or processed
        if (img.dataset.optimizing === 'true') {
            return;
        }
        // Skip if image has already loaded - no point in changing src
        if (img.complete) {
            return;
        }
        // Store original source for fallback
        img.dataset.originalSrc = img.src;
        img.dataset.optimizing = 'true';
        // Generate optimized URL
        const optimizedUrl = this.generateOptimizedUrl(img.src);
        // Set up error handling before changing src
        this.setupErrorHandling(img, img.src);
        // Update image source
        img.src = optimizedUrl;
        // Mark as processed
        this.markProcessed(img);
        delete img.dataset.optimizing;
        this.log(`Optimized image: ${img.dataset.originalSrc}`, 'debug');
    }
    /**
     * Generate optimized URL through proxy
     */
    generateOptimizedUrl(originalUrl) {
        try {
            // Determine optimal format
            const format = this.getOptimalFormat();
            // If no modern format is supported, skip proxying
            if (format === null) {
                this.log(`No modern format support detected, skipping proxy for: ${originalUrl}`, 'debug');
                return originalUrl;
            }
            const imageUrl = new URL(originalUrl, window.location.href);
            const encodedImageUrl = encodeURIComponent(imageUrl.href);
            // Build the proxy URL
            const proxyPath = `/10/insecure/plain/${encodedImageUrl}@${format}`;
            const proxyUrl = new URL(proxyPath, this.config.proxyEndpoint);
            // Add required query parameters
            proxyUrl.searchParams.set('p', 'v3');
            proxyUrl.searchParams.set('a', '86400000');
            proxyUrl.searchParams.set('o', encodeURIComponent(window.location.origin));
            proxyUrl.searchParams.set('t', this.config.proxyToken);
            return proxyUrl.href;
        }
        catch (error) {
            this.log(`Failed to generate optimized URL for ${originalUrl}: ${error}`, 'error');
            return originalUrl;
        }
    }
    /**
     * Detect browser support for image formats
     */
    async detectFormatSupport() {
        const webpSupport = await this.checkFormatSupport('webp');
        const avifSupport = await this.checkFormatSupport('avif');
        return {
            webp: webpSupport,
            avif: avifSupport
        };
    }
    /**
     * Check support for a specific format
     */
    checkFormatSupport(format) {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            // Test images for format support
            const testImages = {
                webp: 'data:image/webp;base64,UklGRkAAAABXRUJQVlA4WAoAAAAQAAAAAAAAAAAAQUxQSAIAAAAAAFZQOCAYAAAAMAEAnQEqAQABAAIANCWkAANwAP77/VAA',
                avif: 'data:image/avif;base64,AAAAHGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZgAAAPBtZXRhAAAAAAAAAChoZGxyAAAAAAAAAABwaWN0AAAAAAAAAAAAAAAAbGliYXZpZgAAAAAOcGl0bQAAAAAAAQAAAB5pbG9jAAAAAEQAAAEAAQAAAAEAAAEUAAAAFQAAAChpaW5mAAAAAAABAAAAGmluZmUCAAAAAAEAAGF2MDFDb2xvcgAAAABoaXBycAAAAElpcGNvAAAAFGlzcGUAAAAAAAAAAQAAAAEAAAAOcGl4aQAAAAABCAAAAAxhdjFDgQAcAAAAABNjb2xybmNseAABAAEAAQAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAAB1tZGF0EgAKBxgADlgICAkyCB/xgAAghQm0'
            };
            img.src = testImages[format] || '';
        });
    }
    /**
     * Get optimal format based on browser support
     * Returns null if no modern formats are supported (no optimization needed)
     */
    getOptimalFormat() {
        if (this.formatSupport.avif) {
            return 'avif';
        }
        if (this.formatSupport.webp) {
            return 'webp';
        }
        return null;
    }
    /**
     * Check if URL is already proxied
     */
    isProxiedUrl(url) {
        return url.includes('production-image-proxy.reproio.com');
    }
    /**
     * Set up error handling for image loading
     */
    setupErrorHandling(img, originalSrc) {
        const errorHandler = () => {
            if (img.dataset.originalSrc && img.src !== img.dataset.originalSrc) {
                this.log(`Failed to load optimized image, falling back to original: ${originalSrc}`, 'warn');
                img.src = img.dataset.originalSrc;
                // Remove error handler to prevent infinite loop
                img.removeEventListener('error', errorHandler);
            }
        };
        img.addEventListener('error', errorHandler, { once: true });
    }
    /**
     * Check if URL should be proxied (whitelist approach)
     */
    shouldProxy(url) {
        if (!url)
            return false;
        // Check exclude patterns first
        if (this.config.excludePatterns.some(pattern => pattern.test(url))) {
            return false;
        }
        // Extract pathname from URL, handling both absolute and relative URLs
        let pathname;
        try {
            const urlObj = new URL(url, window.location.href);
            pathname = urlObj.pathname.toLowerCase();
        }
        catch {
            // If URL parsing fails, try to extract extension directly
            pathname = url.toLowerCase();
        }
        // Check if URL ends with a supported extension
        return this.config.supportedExtensions.some(ext => pathname.endsWith(ext));
    }
    /**
     * Clean up plugin resources
     */
    async cleanup() {
        await super.cleanup();
        // Disconnect observer
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
        // Restore original images
        const images = getAllImages();
        images.forEach(img => {
            if (img.dataset.originalSrc) {
                img.src = img.dataset.originalSrc;
                delete img.dataset.originalSrc;
            }
        });
    }
}
//# sourceMappingURL=index.js.map